import { motion } from 'framer-motion';
import { ArrowPathIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { UploadJob } from '@/stores/uploadJobStore';

interface UploadProgressCardProps {
  job: UploadJob;
  onCancel: () => void;
  onRetry: () => void;
  onReview: () => void;
}

export function UploadProgressCard({ job, onCancel, onRetry, onReview }: UploadProgressCardProps) {
  const uploading = job.phase === 'creating' || job.phase === 'uploading';
  const transcribing = job.phase === 'transcribing';
  const inFlight = uploading || transcribing;
  const percent = uploading ? job.uploadProgress : job.transcription?.progress ?? 0;

  const heading = uploading
    ? 'Uploading recording'
    : transcribing
      ? 'Transcribing recording…'
      : job.phase === 'complete'
        ? 'Transcription complete'
        : job.phase === 'failed'
          ? 'Upload failed'
          : 'Upload cancelled';

  const Icon = job.phase === 'complete'
    ? CheckCircleIcon
    : job.phase === 'failed'
      ? ExclamationTriangleIcon
      : ArrowPathIcon;

  const iconClass = job.phase === 'complete'
    ? 'text-green-500'
    : job.phase === 'failed'
      ? 'text-red-500'
      : `text-blue-500${inFlight ? ' animate-spin' : ''}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.15 }}
      className="w-full rounded-lg border border-gray-200 bg-white p-3 shadow-sm"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start gap-2">
        <Icon className={`h-5 w-5 shrink-0 ${iconClass}`} aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-700">{heading}</p>
          {job.patientName && <p className="text-xs text-gray-600 break-words">{job.patientName}</p>}
          {job.encounterDate && <p className="text-xs text-gray-500">{job.encounterDate}</p>}
        </div>
      </div>

      {inFlight && (
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded bg-gray-100">
          <div className="h-full rounded bg-blue-500 transition-all" style={{ width: `${Math.min(100, Math.max(0, percent))}%` }} />
        </div>
      )}

      {job.phase === 'failed' && job.error && (
        <p className="mt-1 text-xs text-gray-500 break-words">{job.error}</p>
      )}

      <div className="mt-2 flex justify-end gap-3 text-xs">
        {inFlight && (
          <button type="button" onClick={onCancel} className="text-gray-500 hover:text-red-600">Cancel</button>
        )}
        {job.phase === 'failed' && (
          <button type="button" onClick={onRetry} className="font-medium text-blue-600 hover:underline">Retry</button>
        )}
        {job.phase === 'complete' && (
          <button type="button" onClick={onReview} className="font-medium text-blue-600 hover:underline">Review note</button>
        )}
      </div>
    </motion.div>
  );
}
